import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import WorkCard from "@/components/feature/WorkCard";
import EmptyState from "@/components/base/EmptyState";
import NewsRow from "@/components/feature/NewsRow";
import useSEO from "@/hooks/useSEO";
import useFavorites from "@/hooks/useFavorites";
import {
  getActressById,
  getActressNames,
  getNewsByActress,
  getRelatedActresses,
  getWorksByActress,
} from "@/utils/data";

export default function ActressDetailPage() {
  const { id } = useParams<{ id: string }>();
  const actress = id ? getActressById(id) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const [shareStatus, setShareStatus] = useState<"idle" | "copied" | "failed">("idle");

  const works = actress ? getWorksByActress(actress.id) : [];
  const relatedNews = actress
    ? getNewsByActress(actress.id).sort((a, b) => b.date.localeCompare(a.date)).slice(0, 5)
    : [];
  const relatedActresses = actress ? getRelatedActresses(actress, 4) : [];
  const favorited = actress ? isFavorite(actress.id) : false;

  useSEO({
    title: actress
      ? `${actress.name} - 女优资料、简介与代表作品 - 日本女优大全`
      : "未找到该女优信息 - 日本女优大全",
    description: actress
      ? `${actress.name}（${actress.nameRomaji}），${actress.debutYear} 年出道，所属${actress.agency}。代表作品包括${actress.featuredWorks.join("、")}。查看完整资料、出演作品与相关新闻。`
      : "该女优档案不存在或已调整，请返回女优分类页浏览其他档案。",
    keywords: actress
      ? `${actress.name},${actress.nameRomaji},日本女优资料,${actress.featuredWorks.join(",")}`
      : "日本女优资料,女优分类",
  });

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShareStatus("copied");
    } catch {
      setShareStatus("failed");
    }
    window.setTimeout(() => setShareStatus("idle"), 2400);
  };

  if (!actress) {
    return (
      <SiteLayout>
        <Breadcrumbs items={[{ label: "女优分类", href: "/category" }, { label: "未找到档案" }]} />
        <EmptyState
          icon="ri-user-unfollow-line"
          title="未找到该女优信息"
          description="你访问的女优档案不存在，可能链接有误或资料已调整。可以返回分类页浏览全部女优，或使用顶部搜索查找姓名。"
          actionLabel="返回女优分类"
          actionHref="/category"
        />
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <Breadcrumbs items={[{ label: "女优分类", href: "/category" }, { label: actress.name }]} />

      <section className="grid gap-6 md:grid-cols-[240px_1fr] lg:grid-cols-[280px_1fr] lg:gap-8">
        <div className="relative overflow-hidden rounded-lg border border-background-200 bg-background-200">
          <div className="aspect-[3/4] w-full">
            <img
              src={actress.portrait}
              alt={`${actress.name} 女优资料写真封面`}
              title={`${actress.name} 日本女优资料与影视作品`}
              className="h-full w-full object-cover object-top"
            />
          </div>
          <span
            className={`absolute left-3 top-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-label text-[11px] ${
              actress.status === "活跃"
                ? "bg-accent-500 text-foreground-950"
                : "bg-secondary-500/90 text-background-50"
            }`}
          >
            <i className={actress.status === "活跃" ? "ri-pulse-line" : "ri-pause-line"} aria-hidden="true"></i>
            {actress.status}
          </span>
        </div>

        <div>
          <p className="font-label text-[11px] tracking-[0.2em] text-primary-600">ACTRESS PROFILE</p>
          <h1 className="mt-1.5 font-heading text-[30px] font-black leading-tight text-foreground-950 md:text-[38px]">
            {actress.name}
          </h1>
          <p className="mt-1 font-label text-sm text-foreground-500">
            {actress.nameKana} · {actress.nameRomaji}
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 rounded-md bg-primary-50 px-2.5 py-1 font-label text-xs text-primary-800">
              <i className="ri-building-line" aria-hidden="true"></i>
              {actress.agency}
            </span>
            <span className="inline-flex items-center gap-1 rounded-md bg-secondary-100 px-2.5 py-1 font-label text-xs text-secondary-900">
              <i className="ri-calendar-line" aria-hidden="true"></i>
              {actress.debutYear} 年出道
            </span>
            <span className="inline-flex items-center gap-1 rounded-md bg-accent-100 px-2.5 py-1 font-label text-xs text-accent-900">
              <i className="ri-film-line" aria-hidden="true"></i>
              {actress.worksCount} 部作品
            </span>
          </div>

          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-700">{actress.bio}</p>

          <dl className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5">
              <dt className="font-label text-[10px] text-foreground-500">年龄</dt>
              <dd className="mt-0.5 font-heading text-base font-bold text-foreground-900">{actress.age} 岁</dd>
            </div>
            <div className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5">
              <dt className="font-label text-[10px] text-foreground-500">出生日期</dt>
              <dd className="mt-0.5 font-heading text-base font-bold text-foreground-900">{actress.birthDate}</dd>
            </div>
            <div className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5">
              <dt className="font-label text-[10px] text-foreground-500">身高</dt>
              <dd className="mt-0.5 font-heading text-base font-bold text-foreground-900">{actress.height}</dd>
            </div>
            <div className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5">
              <dt className="font-label text-[10px] text-foreground-500">出身地</dt>
              <dd className="mt-0.5 font-heading text-base font-bold text-foreground-900">{actress.hometown}</dd>
            </div>
          </dl>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => toggleFavorite(actress.id)}
              aria-pressed={favorited}
              className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md px-4 py-2 font-label text-sm transition-colors ${
                favorited
                  ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                  : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"} aria-hidden="true"></i>
              {favorited ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-background-300 px-4 py-2 font-label text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-share-forward-line" aria-hidden="true"></i>
              分享链接
            </button>
            <Link
              to="/category"
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-2 font-label text-sm text-foreground-500 transition-colors hover:text-primary-700"
            >
              <i className="ri-arrow-left-line" aria-hidden="true"></i>
              返回分类页
            </Link>
          </div>
          <p className="mt-2 font-label text-[11px] text-foreground-400">
            收藏功能为前端演示，状态仅保存在当前浏览器。
            {shareStatus === "copied" ? " 链接已复制到剪贴板。" : ""}
            {shareStatus === "failed" ? " 复制失败，请手动复制浏览器地址栏链接。" : ""}
          </p>
        </div>
      </section>

      <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_300px]">
        <div>
          <div className="mb-4 flex items-end justify-between gap-3 border-b border-background-200 pb-3">
            <h2 className="font-heading text-xl font-bold text-foreground-950">
              出演作品
              <span className="ml-2 font-label text-xs font-normal text-foreground-500">
                {works.length} 部相关条目
              </span>
            </h2>
            <Link
              to="/work-list"
              className="font-label text-xs text-primary-700 transition-colors hover:text-primary-600"
            >
              全部作品
            </Link>
          </div>
          {works.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 xl:grid-cols-3">
              {works.map((work) => (
                <WorkCard key={work.id} work={work} actressNames={getActressNames(work)} />
              ))}
            </div>
          ) : (
            <p className="rounded-md bg-background-50 px-4 py-6 text-center text-sm text-foreground-600">
              该女优暂无可展示的作品条目，可前往作品列表浏览其他作品。
            </p>
          )}

          <div className="mb-2 mt-10 flex items-end justify-between gap-3 border-b border-background-200 pb-3">
            <h2 className="font-heading text-xl font-bold text-foreground-950">
              相关新闻
              <span className="ml-2 font-label text-xs font-normal text-foreground-500">
                最新 {relatedNews.length} 条
              </span>
            </h2>
            <Link to="/news" className="font-label text-xs text-primary-700 transition-colors hover:text-primary-600">
              新闻列表
            </Link>
          </div>
          {relatedNews.length > 0 ? (
            <div>
              {relatedNews.map((item) => (
                <NewsRow key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <p className="rounded-md bg-background-50 px-4 py-6 text-center text-sm text-foreground-600">
              暂时没有与该女优直接相关的报道。
            </p>
          )}
        </div>

        <aside className="space-y-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">基本资料</h2>
            <dl className="mt-3 space-y-2.5">
              {[
                { label: "姓名", value: actress.name },
                { label: "假名", value: actress.nameKana },
                { label: "罗马音", value: actress.nameRomaji },
                { label: "生日", value: actress.birthDate },
                { label: "年龄", value: `${actress.age} 岁` },
                { label: "出道年份", value: `${actress.debutYear} 年` },
                { label: "所属事务所", value: actress.agency },
                { label: "身高", value: actress.height },
                { label: "出身地", value: actress.hometown },
                { label: "活跃状态", value: actress.status },
              ].map((row) => (
                <div key={row.label} className="flex items-start justify-between gap-3 border-b border-background-100 pb-2 last:border-b-0 last:pb-0">
                  <dt className="shrink-0 font-label text-[11px] text-foreground-500">{row.label}</dt>
                  <dd className="text-right font-label text-xs text-foreground-900">{row.value}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {actress.genres.map((genre) => (
                <span
                  key={genre}
                  className="rounded bg-secondary-100 px-2 py-0.5 font-label text-[11px] text-secondary-900"
                >
                  {genre}
                </span>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">代表作品</h2>
            <ul className="mt-3 space-y-2">
              {actress.featuredWorks.map((title) => (
                <li key={title} className="flex items-start gap-2 font-label text-xs text-foreground-700">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-accent-600">
                    <i className="ri-bookmark-3-line" aria-hidden="true"></i>
                  </span>
                  {title}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">相关女优推荐</h2>
            <ul className="mt-3 space-y-3">
              {relatedActresses.map((item) => (
                <li key={item.id}>
                  <Link to={`/detail/actress/${item.id}`} className="group flex items-center gap-2.5">
                    <span className="h-10 w-10 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={item.portrait}
                        alt={`${item.name} 女优资料写真`}
                        title={`${item.name} 日本女优资料`}
                        className="h-full w-full object-cover object-top"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-label text-sm text-foreground-900 transition-colors group-hover:text-primary-700">
                        {item.name}
                      </span>
                      <span className="block truncate font-label text-[11px] text-foreground-500">
                        {item.agency} · {item.debutYear} 年出道
                      </span>
                    </span>
                    <i className="ri-arrow-right-s-line text-foreground-300" aria-hidden="true"></i>
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/category"
              className="mt-4 inline-flex w-full items-center justify-center gap-1 rounded-md border border-background-300 py-2 font-label text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              浏览全部分类
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </aside>
      </div>
    </SiteLayout>
  );
}